// lib/supabaseClient.js
import { createBrowserClient } from "@supabase/ssr";
import { fetchWithTimeout } from "./fetchWithTimeout";
import {
  createBrowserSupabaseCookieMethods,
  getBrowserCookieBaseOptions,
} from "./authCookies";
import { isConfirmedSignedOutAuthError } from "./auth/authErrorClassification";

let supabase = null;
let getSessionPromise = null;
let tokenRequestPromise = null;
let authCooldownUntil = 0;
let authBackoffMs = 0;
let authInvalidTokenAt = 0;
let refreshDisabledUntil = 0;
let refreshDisabledReason = null;
let refreshInFlight = null;
let refreshLastAt = 0;
let refreshAttemptSequence = 0;
let refreshTriggerHint = null;
const authGuardListeners = new Set();

const AUTH_BACKOFF_BASE_MS = 30000;
const AUTH_BACKOFF_MAX_MS = 60000;
const AUTH_REFRESH_COOLDOWN_MS = 10000;
const AUTH_INVALID_REFRESH_DISABLE_MS = 60000;
const AUTH_TRANSIENT_REFRESH_DISABLE_MS = 60000;
const AUTH_TRIGGER_HINT_MAX_AGE_MS = 2000;

const authDiagEnabled = () => process.env.NEXT_PUBLIC_AUTH_DIAG === "1";
const debugSupabaseEmailEnabled = () =>
  process.env.NEXT_PUBLIC_DEBUG_SUPABASE_EMAIL === "1";

function isSupabaseEmailEndpoint(urlString) {
  if (!urlString) return false;
  return (
    urlString.includes("/auth/v1/otp") ||
    urlString.includes("/auth/v1/signup") ||
    urlString.includes("/auth/v1/invite")
  );
}

function getUrlDiagnostic(url) {
  const raw = String(url || "");
  try {
    const baseUrl =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "http://localhost";
    const parsed = new URL(raw, baseUrl);
    return {
      requestPathname: parsed.pathname,
      requestSearchKeys: Array.from(parsed.searchParams.keys()),
    };
  } catch {
    return {
      requestCategory:
        raw === "refreshSession" ? "refresh_session" : "unparseable_url",
    };
  }
}

function notifyAuthGuard() {
  const snapshot = getAuthGuardState();
  authGuardListeners.forEach((listener) => listener(snapshot));
}

export function subscribeAuthGuard(listener) {
  authGuardListeners.add(listener);
  return () => {
    authGuardListeners.delete(listener);
  };
}

export function getAuthGuardState() {
  const now = Date.now();
  if (refreshDisabledUntil && now >= refreshDisabledUntil) {
    refreshDisabledUntil = 0;
    refreshDisabledReason = null;
  }
  return {
    cooldownUntil: authCooldownUntil,
    cooldownMsRemaining: Math.max(0, authCooldownUntil - now),
    backoffMs: authBackoffMs,
    tokenInvalidAt: authInvalidTokenAt,
    refreshDisabledUntil,
    refreshDisabledMsRemaining: Math.max(0, refreshDisabledUntil - now),
    refreshDisabledReason,
  };
}

export function acknowledgeAuthTokenInvalid(tokenInvalidAt) {
  if (authInvalidTokenAt && authInvalidTokenAt <= tokenInvalidAt) {
    authInvalidTokenAt = 0;
    notifyAuthGuard();
  }
}

function setAuthCooldown(status, url) {
  const now = Date.now();
  const nextBackoff = authBackoffMs
    ? Math.min(AUTH_BACKOFF_MAX_MS, authBackoffMs * 2)
    : AUTH_BACKOFF_BASE_MS;
  const jitter = Math.floor(nextBackoff * (0.2 * Math.random()));
  authBackoffMs = nextBackoff;
  authCooldownUntil = now + nextBackoff + jitter;
  if (authDiagEnabled()) {
    console.warn("[AUTH_DIAG] auth token rate limited", {
      status,
      ...getUrlDiagnostic(url),
      cooldownMs: authCooldownUntil - now,
    });
  }
  notifyAuthGuard();
}

function setAuthCooldownUntil(cooldownMs, status, url) {
  const now = Date.now();
  const nextUntil = now + cooldownMs;
  authCooldownUntil = Math.max(authCooldownUntil, nextUntil);
  authBackoffMs = Math.max(authBackoffMs, cooldownMs);
  if (authDiagEnabled()) {
    console.warn("[AUTH_DIAG] auth token cooldown", {
      status,
      ...getUrlDiagnostic(url),
      cooldownMs,
    });
  }
  notifyAuthGuard();
}

function clearAuthCooldown() {
  if (!authCooldownUntil && !authBackoffMs) return;
  authCooldownUntil = 0;
  authBackoffMs = 0;
  notifyAuthGuard();
}

function markAuthTokenInvalid(status, url) {
  authInvalidTokenAt = Date.now();
  if (authDiagEnabled()) {
    console.warn("[AUTH_DIAG] auth token invalid", {
      status,
      ...getUrlDiagnostic(url),
    });
  }
  notifyAuthGuard();
}

function setRefreshDisabled(ms, reason, metadata = {}) {
  const now = Date.now();
  const nextUntil = now + ms;
  if (nextUntil <= refreshDisabledUntil) return;
  refreshDisabledUntil = nextUntil;
  refreshDisabledReason = reason || refreshDisabledReason;
  if (authDiagEnabled()) {
    const url = metadata.url;
    const safeMetadata = { ...metadata };
    delete safeMetadata.url;
    delete safeMetadata.message;
    delete safeMetadata.error;
    delete safeMetadata.userId;
    console.warn("[AUTH_DIAG] auth refresh disabled", {
      reason,
      cooldownMs: ms,
      ...safeMetadata,
      ...(url ? getUrlDiagnostic(url) : {}),
    });
  }
  notifyAuthGuard();
}

function isAuthCooldownActive() {
  return Date.now() < authCooldownUntil;
}

function isRefreshDisabled() {
  return Date.now() < refreshDisabledUntil;
}

function clearTransientRefreshCooldown() {
  if (
    refreshDisabledReason !== "transient_network" ||
    Date.now() >= refreshDisabledUntil
  ) {
    return false;
  }
  refreshDisabledUntil = 0;
  refreshDisabledReason = null;
  notifyAuthGuard();
  return true;
}

function rememberRefreshTrigger(source) {
  refreshTriggerHint = {
    source,
    at: Date.now(),
  };
}

function getRefreshInitiatingSource() {
  if (
    refreshTriggerHint &&
    Date.now() - refreshTriggerHint.at <= AUTH_TRIGGER_HINT_MAX_AGE_MS
  ) {
    return refreshTriggerHint.source;
  }
  return "supabase_auto_or_internal";
}

function getBrowserClientInstanceId() {
  if (typeof globalThis === "undefined") return "unavailable";
  if (!globalThis.__ybSupabaseClientId) {
    globalThis.__ybSupabaseClientId =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? `sb-${crypto.randomUUID()}`
        : `sb-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
  return globalThis.__ybSupabaseClientId;
}

function getTransientRefreshFailureCategory(error) {
  const name = String(error?.name || "").toLowerCase();
  const code = String(error?.code || "").toLowerCase();
  const message = String(error?.message || "").toLowerCase();
  if (
    name === "typeerror" ||
    name === "timeouterror" ||
    name === "aborterror" ||
    code === "request_timeout" ||
    Number(error?.status) === 0 ||
    message.includes("load failed") ||
    message.includes("failed to fetch") ||
    message.includes("network") ||
    message.includes("access control")
  ) {
    if (name === "timeouterror" || code === "request_timeout") {
      return "timeout";
    }
    if (name === "aborterror") return "aborted";
    return "network_or_cors";
  }
  return null;
}

function createTransientRefreshResponse(cooldownMs) {
  return new Response(
    JSON.stringify({
      error: "refresh_temporarily_unavailable",
      retry_after_ms: cooldownMs,
    }),
    {
      status: 503,
      headers: {
        "Content-Type": "application/json",
        "Retry-After": String(Math.ceil(cooldownMs / 1000)),
      },
    }
  );
}

function authDiagLog(label, payload = {}) {
  if (!authDiagEnabled() || typeof window === "undefined") return;
  const timestamp = new Date().toISOString();
  const firstSegment = String(window.location.pathname || "")
    .split("/")
    .filter(Boolean)[0];
  console.log("[AUTH_DIAG]", {
    timestamp,
    routeCategory: firstSegment || "root",
    searchKeys: Array.from(new URLSearchParams(window.location.search).keys()),
    label,
    ...payload,
  });
}

function isSupabaseAuthStorageKey(key) {
  return (
    typeof key === "string" &&
    key.startsWith("sb-") &&
    (key.endsWith("-auth-token") || key.endsWith("-auth-token-user"))
  );
}

function looksLikeSessionObject(value) {
  return (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    (typeof value.access_token === "string" ||
      typeof value.refresh_token === "string" ||
      Boolean(value.user?.id))
  );
}

function looksLikeUserStorageObject(value) {
  return (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    Boolean(value.user?.id)
  );
}

export function __normalizeSupabaseAuthStorageValue(rawValue, key) {
  if (!isSupabaseAuthStorageKey(key) || typeof rawValue !== "string" || rawValue === "") {
    return { value: rawValue, changed: false, clear: false, reason: null };
  }

  let parsed;
  try {
    parsed = JSON.parse(rawValue);
  } catch {
    return {
      value: null,
      changed: true,
      clear: true,
      reason: "malformed_json",
    };
  }

  if (looksLikeSessionObject(parsed) || looksLikeUserStorageObject(parsed)) {
    return { value: rawValue, changed: false, clear: false, reason: null };
  }

  if (typeof parsed === "string") {
    try {
      const inner = JSON.parse(parsed);
      if (looksLikeSessionObject(inner) || looksLikeUserStorageObject(inner)) {
        return {
          value: parsed,
          changed: true,
          clear: false,
          reason: "double_serialized_session",
        };
      }
    } catch {
      return {
        value: null,
        changed: true,
        clear: true,
        reason: "double_serialized_malformed_json",
      };
    }
  }

  return {
    value: null,
    changed: true,
    clear: true,
    reason: "invalid_session_shape",
  };
}

function isInvalidRefreshError(error) {
  return isConfirmedSignedOutAuthError(error);
}

function getRequestUrl(input) {
  return (
    typeof input === "string"
      ? input
      : typeof input?.url === "string"
        ? input.url
        : typeof input?.href === "string"
          ? input.href
          : ""
  );
}

function getGrantTypeFromUrl(input) {
  const url = getRequestUrl(input);
  if (!url) return null;

  try {
    const baseUrl =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "http://localhost";
    return new URL(url, baseUrl).searchParams.get("grant_type");
  } catch {
    return null;
  }
}

function getGrantTypeFromBody(init) {
  if (!init?.body) return null;
  const body = init.body;
  if (typeof body === "string") {
    const trimmed = body.trim();
    if (trimmed.startsWith("{")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (typeof parsed?.grant_type === "string") {
          return parsed.grant_type;
        }
      } catch {
        // Fall through to URL-encoded parsing.
      }
    }
    const params = new URLSearchParams(body);
    return params.get("grant_type");
  }
  if (body instanceof URLSearchParams) {
    return body.get("grant_type");
  }
  if (typeof FormData !== "undefined" && body instanceof FormData) {
    return body.get("grant_type");
  }
  if (typeof body?.get === "function") {
    try {
      return body.get("grant_type");
    } catch {
      return null;
    }
  }
  if (
    typeof body === "object" &&
    !Array.isArray(body) &&
    typeof body.grant_type === "string"
  ) {
    return body.grant_type;
  }
  return null;
}

function getGrantType(input, init) {
  return getGrantTypeFromUrl(input) || getGrantTypeFromBody(init);
}

async function readJsonBody(response) {
  try {
    const clone = response.clone();
    return await clone.json();
  } catch {
    return null;
  }
}

async function getCooldownMsFromResponse(response) {
  const retryAfter = response.headers?.get?.("Retry-After");
  if (retryAfter) {
    const retrySeconds = Number(retryAfter);
    if (!Number.isNaN(retrySeconds) && retrySeconds > 0) {
      return retrySeconds * 1000;
    }
  }

  const data = await readJsonBody(response);
  const raw =
    data?.cooldownMs ??
    data?.cooldown_ms ??
    data?.retry_after_ms ??
    data?.retryAfterMs;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

async function responseIndicatesInvalidRefresh(response) {
  if (response.ok) return false;
  const data = await readJsonBody(response);
  return isInvalidRefreshError(data);
}

function createSupabaseFetch() {
  return async (input, init) => {
    const urlString = getRequestUrl(input);
    if (debugSupabaseEmailEnabled() && isSupabaseEmailEndpoint(urlString)) {
      console.error("[supabase-email-trigger]", {
        ...getUrlDiagnostic(urlString),
        method: init?.method || "GET",
      });
    }
    const isTokenRequest =
      getUrlDiagnostic(urlString).requestPathname === "/auth/v1/token";
    const grantType = getGrantType(input, init);
    const isRefreshRequest = grantType === "refresh_token";
    const baseFetch = () =>
      fetchWithTimeout(input, {
        ...(init || {}),
        timeoutMs: isTokenRequest ? 8000 : 15000,
      });

    if (isTokenRequest) {
      if (authDiagEnabled()) {
        authDiagLog("fetch:token", {
          ...getUrlDiagnostic(urlString),
        });
      }

      if (isRefreshRequest && isRefreshDisabled()) {
        const cooldownMs = Math.max(0, refreshDisabledUntil - Date.now());
        authDiagLog("fetch:token:skip", {
          reason: refreshDisabledReason || "refresh_disabled",
          cooldownMs,
          cooldownSuppressed: true,
        });
        if (refreshDisabledReason !== "invalid_token") {
          return createTransientRefreshResponse(cooldownMs);
        }
        return new Response(
          JSON.stringify({
            error: "invalid_refresh_token",
            error_code: "invalid_refresh_token",
          }),
          {
            status: 400,
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
      }

      if (isRefreshRequest && isAuthCooldownActive()) {
        const cooldown = getAuthGuardState();
        authDiagLog("fetch:token:skip", {
          reason: "rate_limited",
          cooldownMs: cooldown.cooldownMsRemaining,
        });
        return createTransientRefreshResponse(cooldown.cooldownMsRemaining);
      }

      if (isRefreshRequest && tokenRequestPromise) {
        authDiagLog("fetch:token:skip", {
          reason: "in_flight",
          cooldownSuppressed: false,
        });
        return tokenRequestPromise;
      }

      const attemptId = isRefreshRequest ? ++refreshAttemptSequence : null;
      const initiatingSource = isRefreshRequest
        ? getRefreshInitiatingSource()
        : null;
      const clientInstanceId = isRefreshRequest
        ? getBrowserClientInstanceId()
        : null;
      const startedAt = isRefreshRequest ? new Date().toISOString() : null;
      if (isRefreshRequest) {
        authDiagLog("fetch:token:start", {
          attemptId,
          initiatingSource,
          clientInstanceId,
          startedAt,
        });
      }
      const doFetch = baseFetch();
      if (!isRefreshRequest) {
        return doFetch;
      }

      let trackedTokenRequest;
      trackedTokenRequest = doFetch
        .then(async (response) => {
          const invalid = await responseIndicatesInvalidRefresh(response);
          let deliveredResponse = response;
          if (response.status === 429) {
            const cooldownMs = await getCooldownMsFromResponse(response);
            if (cooldownMs) {
              setAuthCooldownUntil(cooldownMs, response.status, urlString);
              setRefreshDisabled(cooldownMs, "rate_limit", {
                status: response.status,
                url: urlString,
              });
            } else {
              setAuthCooldown(response.status, urlString);
            }
          } else if (response.status === 400 && !invalid) {
            setAuthCooldown(response.status, urlString);
          }
          if (invalid) {
            markAuthTokenInvalid(response.status, urlString);
            setRefreshDisabled(AUTH_INVALID_REFRESH_DISABLE_MS, "invalid_token", {
              status: response.status,
              url: urlString,
            });
          } else if (!response.ok) {
            const cooldownMs =
              (response.status === 429
                ? await getCooldownMsFromResponse(response)
                : null) || AUTH_TRANSIENT_REFRESH_DISABLE_MS;
            setRefreshDisabled(
              cooldownMs,
              response.status === 429 ? "rate_limit" : "transient_network",
              {
                status: response.status,
                url: urlString,
              }
            );
            // auth-js clears its local session for non-retryable HTTP errors.
            // Reframe any response without semantic invalid-token evidence as
            // retryable so server reconciliation remains authoritative.
            deliveredResponse = createTransientRefreshResponse(cooldownMs);
          } else if (response.ok) {
            clearAuthCooldown();
            if (refreshDisabledReason === "transient_network") {
              refreshDisabledUntil = 0;
              refreshDisabledReason = null;
              notifyAuthGuard();
            }
          }
          authDiagLog("fetch:token:end", {
            attemptId,
            initiatingSource,
            clientInstanceId,
            startedAt,
            endedAt: new Date().toISOString(),
            ok: deliveredResponse.ok,
            status: response.status,
            failureCategory: response.ok
              ? null
              : invalid
                ? "terminal_refresh"
                : "recoverable_http",
          });
          return deliveredResponse;
        })
        .catch((err) => {
          const failureCategory = getTransientRefreshFailureCategory(err);
          if (failureCategory) {
            setRefreshDisabled(
              AUTH_TRANSIENT_REFRESH_DISABLE_MS,
              "transient_network",
              {
                failureCategory,
                errorName: err?.name || "unknown_error",
              }
            );
          }
          authDiagLog("fetch:token:end", {
            attemptId,
            initiatingSource,
            clientInstanceId,
            startedAt,
            endedAt: new Date().toISOString(),
            ok: false,
            errorName: err?.name || "unknown_error",
            failureCategory: failureCategory || "unknown_error",
          });
          throw err;
        })
        .finally(() => {
          if (tokenRequestPromise === trackedTokenRequest) {
            tokenRequestPromise = null;
          }
        });
      tokenRequestPromise = trackedTokenRequest;

      return tokenRequestPromise;
    }

    return baseFetch();
  };
}

export function getCookieName() {
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
  if (!url) return undefined;
  return `sb-${new URL(url).hostname.split(".")[0]}-auth-token`;
}

export function resetSupabaseClient() {
  console.log("Resetting Supabase client singleton");
  supabase = null;
  getSessionPromise = null;
  tokenRequestPromise = null;
  refreshInFlight = null;
  refreshAttemptSequence = 0;
  refreshTriggerHint = null;
  globalThis.__ybSupabaseRefreshLifecycleCleanup?.();
  globalThis.__ybSupabaseRefreshLifecycleCleanup = null;
  if (typeof globalThis !== "undefined") {
    globalThis.__ybSupabaseClient = null;
    globalThis.__ybSupabaseClientId = null;
  }
}

export function getSupabaseBrowserClient() {
  if (typeof window === "undefined") return null;
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const anonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
  if (!url || !anonKey) {
    if (process.env.NODE_ENV !== "production") {
      const missing = [];
      if (!url) missing.push("NEXT_PUBLIC_SUPABASE_URL");
      if (!anonKey) missing.push("NEXT_PUBLIC_SUPABASE_ANON_KEY");
      throw new Error(`Missing Supabase env: ${missing.join(", ")}`);
    }
    return null;
  }
  const globalScope = typeof globalThis !== "undefined" ? globalThis : null;
  if (!supabase && globalScope?.__ybSupabaseClient) {
    supabase = globalScope.__ybSupabaseClient;
  }
  if (supabase) return supabase;

  try {
    supabase = createBrowserClient(
      url,
      anonKey,
      {
        cookieOptions: getBrowserCookieBaseOptions(),
        cookies: createBrowserSupabaseCookieMethods(),
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: false,
        },
        global: {
          fetch: createSupabaseFetch(),
        },
      }
    );
    if (globalScope) {
      globalScope.__ybSupabaseClient = supabase;
      if (!globalScope.__ybSupabaseClientId) {
        getBrowserClientInstanceId();
      }
      if (process.env.NODE_ENV !== "production") {
        console.info("[SUPABASE] browser client created");
      }
    }

    const originalGetSession = supabase.auth.getSession.bind(supabase.auth);
    supabase.auth.getSession = async (...args) => {
      if (getSessionPromise) return getSessionPromise;
      authDiagLog("auth:getSession");
      let trackedSessionRequest;
      trackedSessionRequest = originalGetSession(...args).finally(() => {
        if (getSessionPromise === trackedSessionRequest) {
          getSessionPromise = null;
        }
      });
      getSessionPromise = trackedSessionRequest;
      return getSessionPromise;
    };

    if (typeof supabase.auth.refreshSession === "function") {
      const originalRefreshSession = supabase.auth.refreshSession.bind(
        supabase.auth
      );
      const runRefreshSession = async (source, args = []) => {
        if (refreshInFlight) {
          authDiagLog("auth:refreshSession:skip", {
            reason: "in_flight",
            initiatingSource: source,
          });
          return refreshInFlight;
        }
        if (isRefreshDisabled()) {
          authDiagLog("auth:refreshSession:skip", {
            reason: refreshDisabledReason || "refresh_disabled",
            disabledMsRemaining: Math.max(0, refreshDisabledUntil - Date.now()),
            initiatingSource: source,
            cooldownSuppressed: true,
          });
          return {
            data: { session: null },
            error:
              refreshDisabledReason === "transient_network"
                ? {
                    name: "AuthRetryableFetchError",
                    code: "refresh_temporarily_unavailable",
                    status: 0,
                  }
                : null,
          };
        }
        const now = Date.now();
        if (now - refreshLastAt < AUTH_REFRESH_COOLDOWN_MS) {
          authDiagLog("auth:refreshSession:skip", {
            reason: "cooldown",
            cooldownMs: AUTH_REFRESH_COOLDOWN_MS - (now - refreshLastAt),
            initiatingSource: source,
            cooldownSuppressed: true,
          });
          return { data: { session: null }, error: null };
        }
        rememberRefreshTrigger(source);
        authDiagLog("auth:refreshSession", { initiatingSource: source });
        refreshLastAt = now;
        let trackedRefresh;
        trackedRefresh = originalRefreshSession(...args)
          .then((result) => {
            authDiagLog("auth:refreshSession:end", {
              ok: !result?.error,
              status: result?.error?.status,
              errorName: result?.error?.name || null,
              errorCode: result?.error?.code || null,
            });
            const error = result?.error;
            if (error) {
              if (isInvalidRefreshError(error)) {
                markAuthTokenInvalid(error?.status, "refreshSession");
                setRefreshDisabled(AUTH_INVALID_REFRESH_DISABLE_MS, "invalid_token", {
                  status: error?.status,
                  errorName: error?.name || null,
                  errorCode: error?.code || null,
                });
              }
            }
            return result;
          })
          .catch((err) => {
            authDiagLog("auth:refreshSession:end", {
              ok: false,
              status: err?.status,
              errorName: err?.name || "unknown_error",
              errorCode: err?.code || null,
            });
            if (isInvalidRefreshError(err)) {
              markAuthTokenInvalid(err?.status, "refreshSession");
              setRefreshDisabled(AUTH_INVALID_REFRESH_DISABLE_MS, "invalid_token", {
                status: err?.status,
                errorName: err?.name || null,
                errorCode: err?.code || null,
              });
            }
            throw err;
          })
          .finally(() => {
            if (refreshInFlight === trackedRefresh) {
              refreshInFlight = null;
            }
          });
        refreshInFlight = trackedRefresh;
        return refreshInFlight;
      };
      supabase.auth.refreshSession = (...args) =>
        runRefreshSession("explicit", args);

      globalScope?.__ybSupabaseRefreshLifecycleCleanup?.();
      const handleFocus = () => rememberRefreshTrigger("focus");
      const handleVisibility = () => {
        if (document.visibilityState === "visible") {
          rememberRefreshTrigger("visibility");
        }
      };
      const handleOnline = () => {
        rememberRefreshTrigger("online");
        if (!clearTransientRefreshCooldown()) return;
        refreshLastAt = 0;
        void runRefreshSession("online").catch((error) => {
          authDiagLog("auth:refreshSession:online:error", {
            errorName: error?.name || "unknown_error",
          });
        });
      };
      window.addEventListener("focus", handleFocus);
      document.addEventListener("visibilitychange", handleVisibility);
      window.addEventListener("online", handleOnline);
      if (globalScope) {
        globalScope.__ybSupabaseRefreshLifecycleCleanup = () => {
          window.removeEventListener("focus", handleFocus);
          document.removeEventListener("visibilitychange", handleVisibility);
          window.removeEventListener("online", handleOnline);
        };
      }
    }

      const originalGetUser = supabase.auth.getUser.bind(supabase.auth);
      supabase.auth.getUser = async (...args) => {
        authDiagLog("auth:getUser");
        if (!args[0]) {
          try {
            const { data } = await supabase.auth.getSession();
            if (!data?.session) {
              return { data: { user: null }, error: null };
            }
          } catch {
            return { data: { user: null }, error: null };
          }
        }
        return originalGetUser(...args);
      };

    const wrapAuthMethod = (name) => {
      const original = supabase.auth[name]?.bind(supabase.auth);
      if (!original) return;
      supabase.auth[name] = async (...args) => {
        authDiagLog(`auth:${name}`);
        return original(...args);
      };
    };

    [
      "exchangeCodeForSession",
      "signInWithOAuth",
      "signInWithPassword",
      "signUp",
    ].forEach(wrapAuthMethod);
  } catch (err) {
    console.error("Failed to initialize Supabase browser client", {
      errorName: err?.name || "unknown_error",
    });
    return null;
  }

  return supabase;
}

export function getFreshBrowserSupabaseClient() {
  if (authDiagEnabled()) {
    authDiagLog("auth:client:reuse", {
      message: "Fresh client requested; returning singleton to avoid refresh loops.",
    });
  }
  return getSupabaseBrowserClient();
}

export function clearSupabaseAuthStorage() {
  if (typeof window === "undefined") return;
  const storageKey =
    supabase?.auth?.storageKey ||
    getCookieName() ||
    null;
  try {
    if (storageKey) {
      localStorage.removeItem(storageKey);
      sessionStorage.removeItem(storageKey);
    } else {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith("sb-") && key.endsWith("-auth-token")) {
          localStorage.removeItem(key);
        }
      });
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith("sb-") && key.endsWith("-auth-token")) {
          sessionStorage.removeItem(key);
        }
      });
    }
  } catch (err) {
    if (authDiagEnabled()) {
      authDiagLog("auth:storage:clear:failed", {
        errorName: err?.name || "unknown_error",
      });
    }
  }
}

export function __getSupabaseAuthGrantType(input, init) {
  return getGrantType(input, init);
}

export function __createSupabaseFetchForTests() {
  return createSupabaseFetch();
}
